import React, { useEffect, useState } from "react";
import AdminService from "../../../services/api/admin.service";
import { Card, CardContent } from "@/components/ui/card";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Loader } from "lucide-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    AdminService.getBlogStats()
      .then((data) => {
    
        setStats(data);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen text-blue-600">
        <Loader className="animate-spin w-8 h-8" />
        <span className="ml-2">Loading stats...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 bg-gradient-to-br from-blue-50 via-white to-purple-50 min-h-screen">
      {/* Stats + Chart in one row for desktop */}
      <div className="flex flex-col lg:flex-row gap-6 h-full items-stretch">
        {/* Left side - 1/3 width */}
        <div className="flex flex-col gap-6 w-full lg:w-1/3 h-full">
          <div className="flex flex-col justify-between h-full gap-8 flex-1">
            {/* Total Posts */}
            <div className="flex-1 bg-white rounded-xl p-6 shadow-md border border-gray-100 flex flex-col justify-between transition-all hover:shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Total Posts
                  </p>
                  <p className="text-3xl font-bold text-blue-600 mt-2">
                    {stats.totalPosts}
                  </p>
                </div>
                <div className="bg-blue-100 p-3 rounded-full">
                  <svg
                    className="w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M4 6h16M4 12h16M4 18h16"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Total Views */}
            <div className="flex-1 bg-white rounded-xl p-6 shadow-md border border-gray-100 flex flex-col justify-between transition-all hover:shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Total Views
                  </p>
                  <p className="text-3xl font-bold text-green-600 mt-2">
                    {stats.totalViews}
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - 2/3 width */}
        <div className="w-full lg:w-2/3 h-full flex flex-col">
          <Card className="bg-white shadow-lg rounded-xl h-full flex-1 flex flex-col">
            <CardContent className="p-6 flex-1 flex flex-col">
              <h2 className="text-2xl font-semibold text-purple-600 mb-4">
                Posts by Month
              </h2>
              <div className="w-full h-full min-h-[200px]">
                <Bar
                  data={{
                    labels: stats.postsByMonth.map((item) => item.month),
                    datasets: [
                      {
                        label: "Posts",
                        backgroundColor: "#6366f1",
                        data: stats.postsByMonth.map((item) => item.posts),
                      },
                      {
                        label: "Views",
                        backgroundColor: "#10b981",
                        data: stats.postsByMonth.map((item) => item.views),
                      },
                      {
                        label: "Comments",
                        backgroundColor: "#f43f5e",
                        data: stats.postsByMonth.map((item) => item.comments),
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: "bottom" },
                      title: { display: false },
                    },
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Top 5 Posts Table */}
      <Card className="bg-white shadow-lg rounded-xl">
        <CardContent className="p-6">
          <h2 className="text-2xl font-semibold text-orange-600 mb-4">
            Top 5 Posts
          </h2>
          <div className="overflow-x-auto rounded-lg border">
            <table className="table-auto w-full text-sm text-left text-gray-700">
              <thead className="text-xs text-gray-500 uppercase bg-gray-100">
                <tr>
                  <th className="px-4 py-2">Title</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Views</th>
                  <th className="px-4 py-2">Comments</th>
                  <th className="px-4 py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {stats.topPosts.map((post) => (
                  <tr key={post.id} className="bg-white border-b">
                    <td className="px-4 py-2">{post.title}</td>
                    <td className="px-4 py-2 capitalize">{post.status}</td>
                    <td className="px-4 py-2">{post.views}</td>
                    <td className="px-4 py-2">{post.commentsCount}</td>
                    <td className="px-4 py-2">
                      {new Date(post.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default AdminDashboard;
